import { supabase } from "@/supabase/supabaseClient";
import type {
  Role,
  Permission,
  RoleWithPermissions,
  CreateRoleData,
  UpdateRoleData,
  AssignRoleData,
  UserWithRoles,
} from "@/types/rbac";

export class RBACService {
  // ============================================================================
  // ROLES
  // ============================================================================

  static async getRoles(): Promise<Role[]> {
    const { data, error } = await supabase
      .from("roles")
      .select("*")
      .eq("is_active", true)
      .order("name");

    if (error) throw error;
    return data || [];
  }

  static async getRoleWithPermissions(
    roleId: string
  ): Promise<RoleWithPermissions> {
    const { data: role, error: roleError } = await supabase
      .from("roles")
      .select("*")
      .eq("id", roleId)
      .single();

    if (roleError) throw roleError;

    const { data: permissions, error: permError } = await supabase
      .from("role_permissions")
      .select(
        `
        permission_id,
        permissions (*)
      `
      )
      .eq("role_id", roleId);

    if (permError) throw permError;

    return {
      ...role,
      permissions:
        (permissions as unknown as { permissions: Permission }[])?.map(
          (rp) => rp.permissions
        ) || [],
    };
  }

  static async createRole(roleData: CreateRoleData): Promise<Role> {
    const { permission_ids, ...roleFields } = roleData;

    const { data: role, error: roleError } = await supabase
      .from("roles")
      .insert([roleFields])
      .select()
      .single();

    if (roleError) throw roleError;

    // Assign permissions if provided
    if (permission_ids && permission_ids.length > 0) {
      const rolePermissions = permission_ids.map((permId) => ({
        role_id: role.id,
        permission_id: permId,
      }));

      const { error: permError } = await supabase
        .from("role_permissions")
        .insert(rolePermissions);

      if (permError) throw permError;
    }

    return role;
  }

  static async updateRole(id: string, roleData: UpdateRoleData): Promise<Role> {
    const { permission_ids, ...roleFields } = roleData;

    const { data: role, error: roleError } = await supabase
      .from("roles")
      .update(roleFields)
      .eq("id", id)
      .select()
      .single();

    if (roleError) throw roleError;

    // Update permissions if provided
    if (permission_ids !== undefined) {
      // First, remove all existing permissions
      const { error: deleteError } = await supabase
        .from("role_permissions")
        .delete()
        .eq("role_id", id);

      if (deleteError) throw deleteError;

      // Then add new permissions
      if (permission_ids.length > 0) {
        const rolePermissions = permission_ids.map((permId) => ({
          role_id: id,
          permission_id: permId,
        }));

        const { error: insertError } = await supabase
          .from("role_permissions")
          .insert(rolePermissions);

        if (insertError) throw insertError;
      }
    }

    return role;
  }

  static async deleteRole(id: string): Promise<void> {
    // Check if role is a system role
    const { data: role, error: checkError } = await supabase
      .from("roles")
      .select("is_system")
      .eq("id", id)
      .single();

    if (checkError) throw checkError;
    if (role.is_system) {
      throw new Error("Cannot delete system roles");
    }

    const { error } = await supabase
      .from("roles")
      .update({ is_active: false })
      .eq("id", id);

    if (error) throw error;
  }

  // ============================================================================
  // PERMISSIONS
  // ============================================================================

  static async getPermissions(): Promise<Permission[]> {
    const { data, error } = await supabase
      .from("permissions")
      .select("*")
      .order("resource, action");

    if (error) throw error;
    return data || [];
  }

  static async getPermissionsByResource(
    resource: string
  ): Promise<Permission[]> {
    const { data, error } = await supabase
      .from("permissions")
      .select("*")
      .eq("resource", resource)
      .order("action");

    if (error) throw error;
    return data || [];
  }

  // ============================================================================
  // USER ROLE ASSIGNMENTS
  // ============================================================================

  static async getUserRoles(userId: string): Promise<Role[]> {
    const { data, error } = await supabase
      .from("user_roles")
      .select(
        `
        role_id,
        roles (*)
      `
      )
      .eq("user_id", userId);

    if (error) throw error;
    return (data as unknown as { roles: Role }[])?.map((ur) => ur.roles) || [];
  }

  static async assignRoleToUser(assignData: AssignRoleData): Promise<void> {
    const { error } = await supabase.from("user_roles").insert([
      {
        user_id: assignData.user_id,
        role_id: assignData.role_id,
        assigned_by: (await supabase.auth.getUser()).data.user?.id,
      },
    ]);

    if (error) throw error;
  }

  static async removeRoleFromUser(
    userId: string,
    roleId: string
  ): Promise<void> {
    const { error } = await supabase
      .from("user_roles")
      .delete()
      .eq("user_id", userId)
      .eq("role_id", roleId);

    if (error) throw error;
  }

  // ============================================================================
  // USER PERMISSIONS
  // ============================================================================

  static async getUserPermissions(userId: string): Promise<Permission[]> {
    const { data, error } = await supabase.rpc("get_user_permissions", {
      user_uuid: userId,
    });

    if (error) throw error;

    return (
      (
        data as {
          permission_name: string;
          resource: string;
          action: string;
          description: string;
        }[]
      )?.map((p) => ({
        id: "", // Not needed from RPC
        name: p.permission_name,
        resource: p.resource,
        action: p.action,
        description: p.description,
        created_at: "",
      })) || []
    );
  }

  static async checkUserPermission(
    userId: string,
    permissionName: string
  ): Promise<boolean> {
    const { data, error } = await supabase.rpc("user_has_permission", {
      user_uuid: userId,
      permission_name: permissionName,
    });

    if (error) {
      console.error("Error checking permission:", error);
      return false;
    }

    return data || false;
  }

  // ============================================================================
  // COMPREHENSIVE USER DATA
  // ============================================================================

  static async getUserWithRoles(userId: string): Promise<UserWithRoles> {
    const { data: user, error: userError } = await supabase
      .from("user_profiles")
      .select(
        `
        id,
        email,
        first_name,
        last_name
      `
      )
      .eq("id", userId)
      .maybeSingle();

    if (userError) throw userError;

    const roles = await this.getUserRoles(userId);
    const permissions = await this.getUserPermissions(userId);

    if (!user) {
      const { data: authResult } = await supabase.auth.getUser();
      const fallbackUser = authResult?.user;

      return {
        id: userId,
        email: fallbackUser?.email ?? "",
        first_name: fallbackUser?.user_metadata?.first_name ?? "",
        last_name: fallbackUser?.user_metadata?.last_name ?? "",
        roles,
        permissions,
      };
    }

    return {
      id: user.id,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      roles,
      permissions,
    };
  }
}
